import React, { useEffect, useState } from "react";
import {
  Box, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Typography, Paper, Grid
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { ClipboardList, DollarSign, Calendar as CalendarIcon } from 'lucide-react';

function AdminForm({ onClose, setRates }) {
  const [roomType, setRoomType] = useState('');
  const [breakfastGuests, setBreakfastGuests] = useState(0);
  const [roomsToSell, setRoomsToSell] = useState(0);
  const [price, setPrice] = useState('');
  const [roomOnlyRate, setRoomOnlyRate] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));
  const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
  const [roomTypes, setRoomTypes] = useState([]);

  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:7072";

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
        const response = await fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`);
        const data = await response.json();
        setRoomTypes(data);
        console.log("Fetched rooms:", data); // ✅ DEBUG
      } catch (err) {
        console.error('Failed to fetch room types:', err);
      }
    };
  
    fetchRoomTypes();
  }, [apiURL]);

  const handleSubmit = async () => {
    if (new Date(startDate) > new Date(endDate)) {
      alert("End date must be after or equal to start date.");
      return;
    }

    setLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const payloads = [];
      const user = JSON.parse(localStorage.getItem('user'));
      const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const date = d.toISOString().split('T')[0];
        payloads.push({
          roomType,
          date,
          breakfastGuests,
          roomsToSell,
          price,
          roomOnlyRate,
          propertyId
        });
      }

      await Promise.all(payloads.map(payload =>
        fetch(`${apiURL}/api/rates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
      ));

      setConfirmationMessage('Room policies updated successfully!');
      setTimeout(() => setConfirmationMessage(''), 3000);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Snackbar
        open={!!confirmationMessage}
        autoHideDuration={3000}
        onClose={() => setConfirmationMessage('')}
      >
        <Alert onClose={() => setConfirmationMessage('')} severity="success" sx={{ width: '100%' }}>
          {confirmationMessage}
        </Alert>
      </Snackbar>
      <Box
        component="form"
        onSubmit={e => { e.preventDefault(); handleSubmit(); }}
        sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ClipboardList size={20} /> Room Setup
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Room Type</InputLabel>
            <Select value={roomType} label="Room Type" onChange={(e) => setRoomType(e.target.value)}>
              {(Array.isArray(roomTypes) ? roomTypes : []).map((room) =>
                room && room.roomType ? (
                  <MenuItem key={room._id} value={room.roomType}>{room.roomType}</MenuItem>
                ) : null
              )}
              {roomTypes.length === 0 && <MenuItem disabled>No room types found</MenuItem>}
            </Select>
          </FormControl>
        </Paper>

        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DollarSign size={20} /> Pricing
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Breakfast Included</Typography>
              <TextField
                type="number"
                placeholder="e.g. 2"
                value={breakfastGuests}
                onChange={e => setBreakfastGuests(Number(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Rate with Breakfast ($)</Typography>
              <TextField
                type="number"
                placeholder="e.g. 150"
                value={price}
                onChange={e => setPrice(Number(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Room-Only Rate ($)</Typography>
              <TextField
                type="number"
                placeholder="e.g. 120"
                value={roomOnlyRate}
                onChange={e => setRoomOnlyRate(Number(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon size={20} /> Availability
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                type="number"
                label="Rooms to Sell"
                value={roomsToSell}
                onChange={e => setRoomsToSell(Number(e.target.value))}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </>
  );
}

export default AdminForm;
